import React, { useRef, useEffect, useState } from 'react';
import { translateBlockValue } from 'i18n';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { getPrintableFilterValuesManager } from './helpers/printableFilterManager';

type Props = {
    filters: DashboardFilterT[];
};

const StyFilterBarContainer = styled('div', {
    name: 'StyFilterBarContainer',
})({
    // NOTE: Beware, if you change the height, the PrintPaginationSeparators component
    // will no longer display valid separators, and constants will have to be changed
    // in PrintableGrid/constants
    height: '2.40cm',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    overflowY: 'hidden',
});

const StyFilterBarFilter = styled('div', {
    name: 'StyFilterBarFilter',
})(({ theme }) => ({
    backgroundColor: theme.palette.lightGrey,
    padding: '0.2cm',
    margin: '0 0.1cm 0.1cm 0',
}));

const isVisible = (child: Element, container: Element) => {
    const { bottom, height, top } = child.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return top <= containerRect.top
        ? containerRect.top - top <= height
        : bottom - containerRect.bottom <= height;
};

const PrintableFilterBar = ({ filters }: Props): React.ReactElement => {
    const filtersRef = useRef<HTMLDivElement>(null);
    const [hiddenFilters, setHiddenFilters] = useState(0);
    const { t } = useTranslation();

    const filtersWithValue = filters
        .filter((filter) => filter.value)
        .map((filter) => {
            if (!filter.values && filter.value?.includes('|')) {
                return {
                    ...filter,
                    values: filter.value.split(
                        '|',
                    ) as unknown as FilterValueT[],
                };
            }
            return filter;
        });
    filtersWithValue.sort(
        // TODO StrictNullCheck : Try to remove "!"
        (a, b) => a.value!.split('|').length - b.value!.split('|').length,
    );

    useEffect(() => {
        const container = filtersRef.current;
        if (!container) return;

        // FYI HTMLElement.children is a HTMLCollection, which is not an array..
        const filtered = Array.from(container.children).filter(
            (child) => !isVisible(child, container),
        );
        setHiddenFilters(filtered.length);
    }, []);

    const filtersText =
        filtersWithValue.length > 0
            ? hiddenFilters === 0
                ? t('printPage.filters')
                : // @ts-expect-error [TS migration] Was not detected by flow
                  t('printPage.filtersWithCount', {
                      count: `${filtersWithValue.length - hiddenFilters}/${
                          filtersWithValue.length
                      }`,
                  })
            : '\u00A0'; /* Non-breaking space to preserve layout */

    return (
        <Box
            sx={{
                background: 'white',
                position: 'relative',
                zIndex: 999999999, // dirty hack to make sure the logo is on top of everything else
            }}
        >
            <Typography color="textPrimary" variant="body1" paragraph>
                {filtersText}
            </Typography>
            <StyFilterBarContainer ref={filtersRef}>
                {filtersWithValue.map((singleFilter) => {
                    const FilterValuesManager =
                        getPrintableFilterValuesManager(singleFilter);
                    return (
                        <StyFilterBarFilter
                            key={singleFilter.key}
                            data-testid="filter"
                        >
                            <span>
                                {`${translateBlockValue(singleFilter.field)}: `}
                            </span>
                            <FilterValuesManager singleFilter={singleFilter} />
                        </StyFilterBarFilter>
                    );
                })}
            </StyFilterBarContainer>
        </Box>
    );
};

export default PrintableFilterBar;
